import { toIdString } from './calc';
import type { EditTarget, LandedRow, Ref } from './calc';
import type { CostingCombination, CostingExpandedData, CostingGridRow, CostingRecord } from './store/Costing.type';
import type { BombiixColumnDef, BombiixFillBatchItem } from '../../../../Components/BombiixAGGrid/types';
import type { SelectOption } from './costing-todo-options';

/* Which grid cells are editable, where, and how an edit reaches the server (BM-9220).

   The calculation runs on the server. This module no longer computes anything: it decides
   editability, turns a cell change into the edit the API expects, echoes the typed value locally so
   the input does not flicker back while the request is in flight, and merges the recomputed figures
   the server returns.

   Editability follows where the value is stored:
   - DOCUMENT fields live once per costing, so they are editable on the parent row only and render
     disabled on child rows. Editing one on a child would silently change every sibling.
   - ROW fields live per colourway/size/length combination: editable on the parent row when the
     costing has a single combination (there is no chevron and no child rows to edit), and on the
     child rows once expanded. A parent showing 'Multiple' has no single value to edit. */

/** Grid colId to the API's field name, for the document-level columns. */
export const DOCUMENT_COLUMN_FIELDS: Record<string, string> = {
  supplier: 'supplier',
  factory: 'factory',
  moqNeeded: 'moqNeeded',
  targetCost: 'targetCost',
  shipmentMethod: 'shipmentMethod',
  incoterm: 'incoterm',
  sampleRef: 'sampleRef',
};

/** Grid colId to the API's field name, for the row-level columns. */
export const ROW_COLUMN_FIELDS: Record<string, string> = {
  quantity: 'quantity',
  pricePerUnit: 'pricePerUnit',
  default_dutyPercentage: 'dutyPercentage',
  default_tariffCostPercentage: 'tariffCostPercentage',
  default_freightPercentage: 'freightPercentage',
  default_freightValue: 'freightValue',
  bottom_sellingPriceGross: 'sellingPriceGross',
  bottom_vat: 'vat',
  bottom_customerDiscount: 'customerDiscount',
  bottom_estimatedRetailValue: 'estimatedRetailValue',
};

/** The landed inputs that echo straight onto a landed row while the save is in flight. */
const LANDED_ECHO_FIELDS = ['dutyPercentage', 'tariffCostPercentage', 'freightPercentage', 'freightValue', 'sellingPriceGross', 'vat', 'customerDiscount', 'estimatedRetailValue'];

/** Gates that disable every editable cell regardless of which column it belongs to. */
export interface EditGates {
  canEdit: boolean;
  isSupplierLoggedIn: boolean;
}

/** A dynamic template column edit, addressed by the landedInfo entry's own `_id` and `key`. */
export interface DynamicColumnEdit {
  columnId: string;
  columnKey: string;
}

/** One cell edit as the server expects it: which field, which combination, what value. */
export interface CostingEditPayload {
  field: string;
  value: unknown;
  target?: EditTarget;
  dynamicColumn?: DynamicColumnEdit;
}

/** The recomputed figures the server returns for one costing. */
export interface CostingCalcProjection {
  _id: string;
  productInfo?: CostingRecord['productInfo'];
  sampleRef?: string | null;
  landedInfoArr?: LandedRow[];
  combinations?: CostingCombination[];
}

/**
 * Unwraps what a dropdown cell hands back before it becomes an edit.
 *
 * `SelectInputCell` emits the whole react-select option (`{ label, value, raw }`), but the API works
 * in domain terms. Passing the option straight through made every dropdown write `null`. Non-option
 * values (typed text, numbers) pass through untouched.
 * @param value whatever the cell reported
 * @returns the underlying record, primitive value, or the original input
 */
export function unwrapSelectValue(value: unknown): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
  const option = value as { raw?: unknown; value?: unknown };
  if (option.raw !== undefined) return option.raw;
  if (option.value !== undefined) return option.value;
  return value;
}

/**
 * True when this costing is a Detailed one.
 *
 * Keyed on the positive `isDetailedCosting` flag, not on `!isAverageCosting`. The two are strict
 * complements in stored data (0 contradictory documents across the collection), but a record that
 * sets NEITHER — which is how this module's own fixtures express an ordinary average costing — must
 * not be read as detailed and silently locked. Matches the existing per-field guard in
 * `canEditRowCell` below.
 */
export function isDetailedCostingRow(cost: CostingRecord | undefined): boolean {
  return cost?.isDetailedCosting === true;
}

/**
 * True when the costing is locked, so every cell on the row renders read-only.
 *
 * Two reasons: it has been completed, or it is a Detailed costing.
 *
 * TEMPORARY (2026-08-05): detailed costings are locked wholesale at the product owner's request.
 * A detailed costing's figures are BOM-derived, and only `pricePerUnit` was previously guarded
 * (`canEditRowCell` below) — the rest of the row was editable and could drift from the BOM. Remove
 * the `isDetailedCostingRow` clause to re-enable them; nothing else needs changing, because both
 * `canEditDocumentCell` and `canEditRowCell` gate on this one function.
 */
export function isCostingLocked(cost: CostingRecord | undefined): boolean {
  if (cost?.markAsComplete === true || cost?.status === 'Complete') return true;
  return isDetailedCostingRow(cost);
}

/**
 * Resolves the combination a grid row addresses.
 * A parent row addresses its single combination; once expanded it addresses none, because the value
 * shown is the literal 'Multiple'.
 */
export function resolveEditTarget(row: CostingGridRow | undefined): EditTarget | null {
  if (!row) return null;
  const source: CostingExpandedData | undefined = row.__rowType === 'child' ? row.expData : row.hasExpanded ? undefined : row.rowOneData;
  if (!source) return null;
  return {
    colourwayId: toIdString(source.colourway ?? null),
    size: source.size ?? null,
    lengthId: toIdString(source.length ?? null),
  };
}

/** True when a document-level cell may be edited on this row. */
export function canEditDocumentCell(row: CostingGridRow | undefined, gates: EditGates): boolean {
  if (!row || !gates.canEdit) return false;
  if (isCostingLocked(row.cost)) return false;
  return row.__rowType === 'parent';
}

/**
 * True when a row-level cell may be edited on this row.
 * Price per unit is additionally BOM-derived on a detailed costing, so it is never editable there.
 */
export function canEditRowCell(row: CostingGridRow | undefined, field: string, gates: EditGates): boolean {
  if (!row || !gates.canEdit) return false;
  if (isCostingLocked(row.cost)) return false;
  if (field === 'pricePerUnit' && row.cost?.isDetailedCosting) return false;
  if (gates.isSupplierLoggedIn && field !== 'quantity' && field !== 'pricePerUnit') return false;
  return resolveEditTarget(row) !== null;
}

/** Reads the landed row a grid row addresses, so an editable cell shows its stored input. */
export function readLandedRow(row: CostingGridRow | undefined): LandedRow | undefined {
  if (!row) return undefined;
  return (row.__rowType === 'child' ? row.expData?.landedData : row.hasExpanded ? undefined : row.rowOneData?.landedData) as LandedRow | undefined;
}

/** The `colourway|length|size` key the list endpoint groups landed rows by. */
function combinationKey(colourwayId: string | null, lengthId: string | null, size: string | null | undefined): string {
  return `${colourwayId ?? ''}|${lengthId ?? ''}|${size ?? ''}`;
}

/** Keys one of the server's combination entries. */
function keyOfCombination(entry: CostingCombination): string {
  return combinationKey(entry.colourwayId, entry.lengthId, entry.size);
}

/** Keys one landed row as the server returns it, with its references already flattened to ids. */
function keyOfLandedRow(row: LandedRow): string {
  return combinationKey(toIdString((row.colourway ?? row.colourWay ?? null) as Ref | string | null), toIdString((row.length ?? null) as Ref | string | null), row.size ?? null);
}

/** Keys one `expandedData` entry, whose references arrive populated. */
function keyOfExpandedData(entry: CostingExpandedData): string {
  return combinationKey(toIdString(entry.colourway ?? null), toIdString(entry.length ?? null), entry.size ?? null);
}

/**
 * Reads the stored price per unit off the combination a grid row addresses.
 * Comes from the `combinations` array the server maintains — the same value the edit writes — so the
 * cell echoes what was typed. The neighbouring Price Per Unit (Incoterms) column shows the derived
 * incoterm roll-up instead and is read-only for that reason.
 * @param row the grid row
 * @returns the stored value as a string, or '' when the row addresses no single combination
 */
export function readCombinationPricePerUnit(row: CostingGridRow | undefined): string {
  const target = resolveEditTarget(row);
  if (!target) return '';
  const wanted = combinationKey(target.colourwayId, target.lengthId, target.size);
  const match = (row?.cost?.combinations ?? []).find((entry) => keyOfCombination(entry) === wanted);
  return match?.pricePerUnit ?? '';
}

/**
 * Reads the stored quantity off the combination a grid row addresses.
 *
 * Reads `combinations` rather than the landed row for the same reason Price Per Unit does: it is the
 * array the edit writes and the server returns unfiltered. The landed row's `quantityPerSizCol` is a
 * grand-total projection, and `getFilteredCombinations` drops any average-costing row lacking BOTH a
 * price and a quantity above zero — so a quantity typed into a combination with no price was stored
 * correctly, never reached the landed row, and the cell blanked itself on the next repaint.
 *
 * Falls back to the landed row so a payload carrying no `combinations` still renders its figure.
 * @param row the grid row
 * @returns the stored quantity as a string, or '' when neither source carries one
 */
export function readCombinationQuantity(row: CostingGridRow | undefined): string {
  const target = resolveEditTarget(row);
  if (!target) return '';
  const wanted = combinationKey(target.colourwayId, target.lengthId, target.size);
  const match = (row?.cost?.combinations ?? []).find((entry) => keyOfCombination(entry) === wanted);
  if (match?.quantityPerSizCol != null) return String(match.quantityPerSizCol);
  const landed = readLandedRow(row) as Record<string, unknown> | undefined;
  return landed?.quantityPerSizCol == null ? '' : String(landed.quantityPerSizCol);
}

/** Reads one dynamic template column's stored input off the landed row a grid row addresses. */
export function readDynamicColumnValue(row: CostingGridRow | undefined, columnId: string | undefined, columnKey: string | undefined): string {
  const landed = readLandedRow(row);
  const column = landed?.landedInfo?.find((entry) => entry?._id === columnId && entry?.key === columnKey);
  return column?.tempValue == null ? '' : String(column.tempValue);
}

/**
 * Turns one grid cell change into the edit the server expects.
 * Only the field name, the combination it targets and the value travel — never a derived figure, and
 * never a whole document. Dropdown values are reduced to their id, because the server resolves the
 * supplier's currency cascade and the factory's MOQ from its own masters.
 * @returns the edit, or null when the cell does not map to an editable field
 */
export function buildCostingEdit(colId: string, value: unknown, row: CostingGridRow, dynamicColumn?: DynamicColumnEdit): CostingEditPayload | null {
  const documentField = DOCUMENT_COLUMN_FIELDS[colId];
  const rowField = dynamicColumn ? 'dutyPercentage' : ROW_COLUMN_FIELDS[colId];
  if (!documentField && !rowField) return null;

  const target = documentField ? undefined : resolveEditTarget(row);
  if (!documentField && !target) return null;

  const unwrapped = unwrapSelectValue(value);
  /* Reference-valued columns are sent as bare ids; everything else as typed. */
  const isReference = documentField === 'supplier' || documentField === 'factory' || documentField === 'incoterm' || documentField === 'sampleRef';
  const editValue = isReference ? toIdString(unwrapped as Ref | string | null) : unwrapped;

  return {
    field: (documentField ?? rowField) as string,
    value: editValue,
    ...(target ? { target } : {}),
    ...(dynamicColumn ? { dynamicColumn } : {}),
  };
}

/** Writes the typed value onto the landed rows of the combination an edit targets. */
function echoOntoLandedRows(cost: CostingRecord, target: EditTarget, patch: (landed: LandedRow) => LandedRow): CostingExpandedData[] {
  const wanted = combinationKey(target.colourwayId, target.lengthId, target.size);
  return (cost.expandedData ?? []).map((entry) => {
    if (!entry.landedData || keyOfExpandedData(entry) !== wanted) return entry;
    return { ...entry, landedData: patch(entry.landedData as unknown as LandedRow) as unknown as NonNullable<CostingExpandedData['landedData']> };
  });
}

/**
 * Echoes a typed value into the loaded costing so the cell holds what the user entered while the
 * save is in flight.
 *
 * Inputs only — nothing derived is touched, because the server owns every derived figure and its
 * response replaces them wholesale a moment later. Without this the cell would repaint from the
 * pre-edit record on the next render and appear to reject the keystroke.
 * @param cost the costing behind the edited row
 * @param edit the edit just built for that cell
 * @param row the grid row, for the combination it addresses
 * @returns a new costing carrying the typed value, or the original when nothing echoes
 */
export function applyLocalInput(cost: CostingRecord, edit: CostingEditPayload, row: CostingGridRow): CostingRecord {
  const { field, value, target, dynamicColumn } = edit;
  const text = value == null ? '' : String(value);

  if (field === 'moqNeeded' || field === 'targetCost' || field === 'shipmentMethod') {
    return { ...cost, productInfo: { ...(cost.productInfo ?? {}), [field]: text } };
  }
  if (field === 'supplier') {
    /* The currency cascade and the factory clear are the server's to make; only the chosen supplier
       is echoed, so the cell keeps its selection until the response lands. */
    return { ...cost, productInfo: { ...(cost.productInfo ?? {}), supplierId: text || null } };
  }
  if (field === 'factory') {
    return { ...cost, productInfo: { ...(cost.productInfo ?? {}), factoryId: text || null } };
  }
  if (field === 'incoterm') {
    return { ...cost, productInfo: { ...(cost.productInfo ?? {}), incoterms: text || null } };
  }
  if (field === 'sampleRef') {
    return { ...cost, sampleRef: text ? { _id: text } : {} };
  }

  const editTarget = target ?? resolveEditTarget(row);
  if (!editTarget) return cost;

  if (dynamicColumn) {
    return {
      ...cost,
      expandedData: echoOntoLandedRows(cost, editTarget, (landed) => ({
        ...landed,
        landedInfo: (landed.landedInfo ?? []).map((column) => (column?._id === dynamicColumn.columnId && column?.key === dynamicColumn.columnKey ? { ...column, tempValue: text } : column)),
      })),
    };
  }
  if (field === 'quantity') {
    /* Echoed onto `combinations` because that is what the Quantity column reads; the landed row is
       written too so the rest of the landed subtree stays consistent while the save is in flight. */
    const wanted = combinationKey(editTarget.colourwayId, editTarget.lengthId, editTarget.size);
    return {
      ...cost,
      combinations: (cost.combinations ?? []).map((entry) => (keyOfCombination(entry) === wanted ? { ...entry, quantityPerSizCol: text } : entry)),
      expandedData: echoOntoLandedRows(cost, editTarget, (landed) => ({ ...landed, quantityPerSizCol: text })),
    };
  }
  if (field === 'pricePerUnit') {
    const wanted = combinationKey(editTarget.colourwayId, editTarget.lengthId, editTarget.size);
    return { ...cost, combinations: (cost.combinations ?? []).map((entry) => (keyOfCombination(entry) === wanted ? { ...entry, pricePerUnit: text } : entry)) };
  }
  if (LANDED_ECHO_FIELDS.includes(field)) {
    return { ...cost, expandedData: echoOntoLandedRows(cost, editTarget, (landed) => ({ ...landed, [field]: text })) };
  }
  return cost;
}

/**
 * Merges the server's recomputed figures back onto a loaded costing.
 * The landed rows carry every derived column the grid renders, so they replace `expandedData`'s
 * `landedData` wholesale; an entry the server did not return keeps what it had rather than blanking.
 * @param cost the costing as currently loaded
 * @param projection the server's response for that costing
 * @returns a new costing showing the server's figures
 */
export function applyCalcProjection(cost: CostingRecord, projection: CostingCalcProjection): CostingRecord {
  const landedByKey = new Map((projection.landedInfoArr ?? []).map((landed) => [keyOfLandedRow(landed), landed]));

  const expandedData = (cost.expandedData ?? []).map((entry) => {
    const matched = landedByKey.get(keyOfExpandedData(entry));
    if (!matched) return entry;
    /* The stored row's references are flattened ids; the loaded entry's are populated objects the
       label columns read, so the identity fields are kept and only the figures replaced. */
    return {
      ...entry,
      landedData: { ...matched, colourway: entry.colourway ?? null, length: entry.length ?? null, size: entry.size ?? null } as unknown as NonNullable<CostingExpandedData['landedData']>,
    };
  });

  return {
    ...cost,
    expandedData,
    ...(projection.combinations ? { combinations: projection.combinations } : {}),
    ...(projection.productInfo ? { productInfo: { ...(cost.productInfo ?? {}), ...projection.productInfo } } : {}),
    ...(projection.sampleRef !== undefined ? { sampleRef: projection.sampleRef ? { ...(cost.sampleRef ?? {}), _id: projection.sampleRef } : {} } : {}),
  };
}

/**
 * Reads a cell's value, preferring what a fill or paste has just written onto the row object.
 *
 * AG Grid writes the dropped value onto `row[colId]` and then reads the cell back through its
 * getter; a getter that ignored that key would report the stored value, and the grid wrapper would
 * drop the change as a no-op. Rows are rebuilt from `costingData` on every state change, so the
 * key lives only until the echo lands or the fill is refused.
 * @param row the grid row
 * @param colId the column whose value is read
 * @param read the stored value, used when no fill is pending on the cell
 * @returns the pending fill value (null when the cell was cleared) or the stored value
 */
export function readCellValue<T>(row: CostingGridRow, colId: string, read: (row: CostingGridRow) => T): T | null {
  const scratch = row as unknown as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(scratch, colId)) return scratch[colId] as T | null;
  return read(row);
}

/** One accepted fill cell: the edit to queue and the row it addresses. */
export interface CostingFillEdit {
  costingId: string;
  edit: CostingEditPayload;
  row: CostingGridRow;
}

/**
 * The value a cell accepts from a fill or paste, or undefined when its own control would refuse it.
 * A dropdown takes only an option the row offers, and is cleared only when its control can be
 * cleared (Sample Ref); every other editable cell holds a number, and a cleared one becomes the
 * empty input the control would send.
 */
function acceptFillValue(column: BombiixColumnDef<CostingGridRow>, row: CostingGridRow, value: unknown): unknown {
  if (column.cellType === 'select') {
    const id = toIdString(unwrapSelectValue(value) as Ref | string | null);
    const props = { ...(column.cellProps ?? {}), ...(column.getCellProps ? column.getCellProps(row) : {}) } as { options?: SelectOption[]; isClearable?: boolean };
    if (!id) return props.isClearable ? null : undefined;
    return (props.options ?? []).some((option) => String(option.value) === id) ? id : undefined;
  }
  if (value === null || value === undefined || value === '') return '';
  const text = String(value).trim();
  return Number.isFinite(Number(text)) ? text : undefined;
}

/**
 * Turns one fill-handle drag or paste into the edits the server expects, in column order.
 *
 * Each cell is checked the way its own control would check it, against the row as the earlier
 * cells of the same drag leave it — so a factory dropped next to a supplier is checked against that
 * supplier. Cells that fail are skipped; the grid rebuilds its rows afterwards, which drops the
 * refused value.
 * @param batch the cells AG Grid wrote, with their rows as they were before the drag
 * @param columns the grid's column definitions, for routing and per-row options
 * @returns the edits to echo and queue, each with the row it addresses
 */
export function buildFillEdits(batch: BombiixFillBatchItem<CostingGridRow>[], columns: BombiixColumnDef<CostingGridRow>[]): CostingFillEdit[] {
  const columnIds = columns.map((column) => column.colId ?? String(column.field));
  const workingCosts = new Map<string, CostingRecord>();
  const edits: CostingFillEdit[] = [];
  [...batch]
    .sort((first, second) => columnIds.indexOf(first.field) - columnIds.indexOf(second.field))
    .forEach((item) => {
      const column = columns[columnIds.indexOf(item.field)];
      const costingId = item.data.cost?._id;
      if (!column || !costingId) return;
      const row: CostingGridRow = { ...item.data, cost: workingCosts.get(costingId) ?? item.data.cost };
      const value = acceptFillValue(column, row, item.newValue);
      if (value === undefined || !row.cost) return;
      const columnProps = column.cellProps as { dynamicColumnId?: string; dynamicColumnKey?: string } | undefined;
      const dynamicColumn = columnProps?.dynamicColumnId ? { columnId: columnProps.dynamicColumnId, columnKey: columnProps.dynamicColumnKey ?? '' } : undefined;
      const edit = buildCostingEdit(item.field, value, row, dynamicColumn);
      if (!edit) return;
      workingCosts.set(costingId, applyLocalInput(row.cost, edit, row));
      edits.push({ costingId, edit, row });
    });
  return edits;
}
